import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence, useMotionValue, useTransform, useSpring } from 'framer-motion';

// ─── ORIGINAL VERSION ───────────────────────────────────────────────────────

const stages = [
  { time: 0, stage: 0, message: 'Initializing AI analysis...', substeps: ['Connecting to compliance engine', 'Loading industry data'] },
  { time: 8, stage: 1, message: 'Researching industry regulations...', substeps: ['Analyzing sector-specific requirements', 'Reviewing legal frameworks', 'Identifying mandatory standards'] },
  { time: 20, stage: 2, message: 'Analyzing your business context...', substeps: ['Processing team structure', 'Evaluating work environment', 'Assessing operational risks'] },
  { time: 35, stage: 3, message: 'Identifying compliance requirements...', substeps: ['Matching regulations to your operations', 'Prioritizing critical requirements', 'Building requirement library'] },
  { time: 52, stage: 4, message: 'Generating training courses...', substeps: ['Creating mandatory training programs', 'Assigning certification requirements', 'Setting renewal periods'] },
  { time: 68, stage: 5, message: 'Creating checklists and documentation...', substeps: ['Designing inspection checklists', 'Generating policy templates', 'Building audit frameworks'] },
  { time: 82, stage: 6, message: 'Finalizing requirements...', substeps: ['Validating compliance coverage', 'Optimizing requirement structure', 'Preparing recommendations'] },
];

function OriginalVersion() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState({ stage: 0, message: 'Initializing AI analysis...', substeps: [] });
  useEffect(() => {
    if (!isGenerating) { setGenerationProgress({ stage: 0, message: 'Initializing AI analysis...', substeps: [] }); return; }
    let currentStage = 0;
    const startTime = Date.now();
    const update = () => {
      const elapsed = (Date.now() - startTime) / 1000;
      for (let i = stages.length - 1; i >= 0; i--) {
        if (elapsed >= stages[i].time) { if (currentStage !== stages[i].stage) { currentStage = stages[i].stage; setGenerationProgress(stages[i]); } break; }
      }
      if (elapsed > 92) setIsGenerating(false);
    };
    const interval = setInterval(update, 500); update();
    return () => clearInterval(interval);
  }, [isGenerating]);

  return (
    <div>
      <div className='mb-6 flex gap-4 justify-center'>
        <Button onClick={() => setIsGenerating(true)} disabled={isGenerating}>Start</Button>
        <Button variant='outline' onClick={() => setIsGenerating(false)} disabled={!isGenerating}>Stop</Button>
      </div>
      <div className='w-full max-w-md mx-auto bg-gray-50 rounded-xl border border-gray-200 shadow-sm' style={{ height: 500 }}>
        <div className='flex flex-col items-center justify-center h-full px-6'>
          {isGenerating ? (
            <div className='text-center max-w-md'>
              <div className='relative mb-6'>
                <div className='absolute inset-0 flex items-center justify-center'><div className='h-16 w-16 rounded-full bg-gradient-to-tr from-purple-600 via-blue-500 to-green-500 animate-pulse opacity-20' /></div>
                <Loader2 className='h-12 w-12 animate-spin text-purple-600 mx-auto relative' />
              </div>
              <p className='text-gray-900 text-base font-semibold mb-2'>{generationProgress.message}</p>
              <p className='text-gray-600 text-sm mb-4'>Our AI is reviewing your business information...</p>
              <div className='mt-6 space-y-2.5 text-left'>
                {generationProgress.substeps.map((step, i) => (
                  <div key={i} className='flex items-center gap-3 text-xs text-gray-700'>
                    <div className='h-1.5 w-1.5 rounded-full bg-purple-600 animate-pulse' style={{ animationDelay: `${i * 0.2}s` }} />
                    <span>{step}</span>
                  </div>
                ))}
              </div>
              <div className='mt-8 w-full bg-gray-200 rounded-full h-1.5 overflow-hidden'>
                <div className='h-full bg-gradient-to-r from-purple-600 to-blue-500 transition-all duration-1000 ease-out' style={{ width: `${(generationProgress.stage / 6) * 100}%` }} />
              </div>
              <p className='text-gray-400 text-xs mt-4'>Stage {generationProgress.stage + 1} of 7</p>
            </div>
          ) : (
            <p className='text-gray-400 text-sm'>Click "Start" to preview</p>
          )}
        </div>
      </div>
    </div>
  );
}


// ─── NEW VERSION V2 ─────────────────────────────────────────────────────────

const v2Messages = [
  { time: 0,  text: 'Initialising compliance engine' },
  { time: 3,  text: 'Loading industry regulation databases' },
  { time: 7,  text: 'Parsing Health & Safety legislation' },
  { time: 11, text: 'Cross-referencing EU compliance directives' },
  { time: 15, text: 'Identifying mandatory reporting standards' },
  { time: 19, text: 'Analysing team roles and structure' },
  { time: 23, text: 'Evaluating operational risk factors' },
  { time: 27, text: 'Mapping department responsibilities' },
  { time: 31, text: 'Matching regulations to your operations' },
  { time: 35, text: 'Prioritising critical training gaps' },
  { time: 39, text: 'Building requirement dependency tree' },
  { time: 44, text: 'Creating mandatory course curriculum' },
  { time: 49, text: 'Setting certification renewal schedules' },
  { time: 54, text: 'Designing inspection checklists' },
  { time: 59, text: 'Generating policy document frameworks' },
  { time: 64, text: 'Running compliance coverage analysis' },
  { time: 69, text: 'Optimising requirement structure' },
  { time: 74, text: 'Validating against best practices' },
  { time: 78, text: 'Preparing your personalised recommendations' },
];

// DNA Helix animation - two intertwined strands, properly centered
function DNAHelix({ isActive }) {
  const count = 12;
  const spread = 28; // how far left/right each dot travels from center
  return (
    <div className="relative h-40 mx-auto" style={{ width: spread * 2 + 8 }}>
      {Array.from({ length: count }).map((_, i) => {
        const delay = i * 0.15;
        const yPos = (i / count) * 100;
        return (
          <div key={i} className="absolute left-1/2 -translate-x-1/2" style={{ top: `${yPos}%` }}>
            {/* Left strand */}
            <motion.div
              className="absolute w-2.5 h-2.5 rounded-full -translate-x-1/2 -translate-y-1/2"
              style={{ background: `hsl(${250 + i * 8}, 80%, 65%)` }}
              animate={isActive ? {
                x: [0, spread, 0, -spread, 0],
                scale: [0.5, 1.1, 0.5, 1.1, 0.5],
                opacity: [0.3, 1, 0.3, 1, 0.3],
              } : { x: 0, scale: 0.5, opacity: 0.2 }}
              transition={{ duration: 2.4, repeat: Infinity, delay, ease: 'easeInOut' }}
            />
            {/* Right strand */}
            <motion.div
              className="absolute w-2.5 h-2.5 rounded-full -translate-x-1/2 -translate-y-1/2"
              style={{ background: `hsl(${190 + i * 6}, 75%, 55%)` }}
              animate={isActive ? {
                x: [0, -spread, 0, spread, 0],
                scale: [0.5, 1.1, 0.5, 1.1, 0.5],
                opacity: [0.3, 1, 0.3, 1, 0.3],
              } : { x: 0, scale: 0.5, opacity: 0.2 }}
              transition={{ duration: 2.4, repeat: Infinity, delay, ease: 'easeInOut' }}
            />
            {/* Connecting bar */}
            <motion.div
              className="absolute top-0 left-0 -translate-x-1/2 -translate-y-1/2 h-[1px] bg-gradient-to-r from-violet-400/40 via-white/10 to-cyan-400/40"
              animate={isActive ? {
                width: ['0px', `${spread * 2}px`, '0px', `${spread * 2}px`, '0px'],
                opacity: [0, 0.4, 0, 0.4, 0],
              } : { width: '0px', opacity: 0 }}
              transition={{ duration: 2.4, repeat: Infinity, delay, ease: 'easeInOut' }}
            />
          </div>
        );
      })}
    </div>
  );
}

// Waveform visualiser
function WaveformBar({ index, isActive, total }) {
  const baseHeight = 4;
  return (
    <motion.div
      className="w-[3px] rounded-full origin-bottom"
      style={{
        background: `linear-gradient(to top, hsl(${250 + (index / total) * 80}, 80%, 60%), hsl(${250 + (index / total) * 80}, 60%, 80%))`,
      }}
      animate={isActive ? {
        height: [
          `${baseHeight}px`,
          `${baseHeight + Math.random() * 28 + 8}px`,
          `${baseHeight + Math.random() * 12}px`,
          `${baseHeight + Math.random() * 32 + 4}px`,
          `${baseHeight}px`,
        ],
      } : { height: `${baseHeight}px` }}
      transition={{
        duration: 0.8 + Math.random() * 0.6,
        repeat: Infinity,
        delay: index * 0.04,
        ease: 'easeInOut',
      }}
    />
  );
}

function Waveform({ isActive }) {
  const barCount = 32;
  return (
    <div className="flex items-end justify-center gap-[2px] h-10">
      {Array.from({ length: barCount }).map((_, i) => (
        <WaveformBar key={i} index={i} isActive={isActive} total={barCount} />
      ))}
    </div>
  );
}

// Typing cursor message
function TypingMessage({ text }) {
  const [displayed, setDisplayed] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    setDisplayed('');
    let i = 0;
    const interval = setInterval(() => {
      i++;
      setDisplayed(text.slice(0, i));
      if (i >= text.length) clearInterval(interval);
    }, 22);
    return () => clearInterval(interval);
  }, [text]);

  return (
    <span>
      {displayed}
      {displayed.length < text.length && (
        <motion.span
          className="inline-block w-[1.5px] h-[1.1em] bg-violet-500 ml-[1px] align-middle rounded-full"
          animate={{ opacity: [1, 0] }}
          transition={{ duration: 0.4, repeat: Infinity }}
        />
      )}
    </span>
  );
}

// Smooth counter
function SmoothCounter({ value }) {
  const spring = useSpring(0, { stiffness: 50, damping: 20 });
  const display = useTransform(spring, v => Math.round(v));
  const [text, setText] = useState('0');

  useEffect(() => { spring.set(value); }, [value, spring]);
  useEffect(() => {
    const unsub = display.on('change', v => setText(String(v)));
    return unsub;
  }, [display]);

  return <span>{text}</span>;
}

function NewVersion() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [msgIdx, setMsgIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const rafRef = useRef(null);
  const startRef = useRef(null);

  useEffect(() => {
    if (!isGenerating) {
      setMsgIdx(0);
      setProgress(0);
      startRef.current = null;
      return;
    }

    startRef.current = Date.now();
    let prevIdx = -1;

    const tick = () => {
      const elapsed = (Date.now() - startRef.current) / 1000;

      // Find current message index
      let idx = 0;
      for (let i = v2Messages.length - 1; i >= 0; i--) {
        if (elapsed >= v2Messages[i].time) { idx = i; break; }
      }

      // Smooth progress
      const totalTime = v2Messages[v2Messages.length - 1].time + 6;
      const p = Math.min((elapsed / totalTime) * 100, 100);
      setProgress(p);

      if (idx !== prevIdx) {
        prevIdx = idx;
        setMsgIdx(idx);
      }

      if (elapsed > totalTime) {
        setProgress(100);
        setTimeout(() => setIsGenerating(false), 800);
        return;
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [isGenerating]);

  const currentMsg = v2Messages[msgIdx];

  return (
    <div>
      <div className='mb-6 flex gap-4 justify-center'>
        <Button onClick={() => setIsGenerating(true)} disabled={isGenerating}>Start</Button>
        <Button variant='outline' onClick={() => setIsGenerating(false)} disabled={!isGenerating}>Stop</Button>
      </div>

      <div className='w-full max-w-md mx-auto rounded-2xl shadow-2xl overflow-hidden relative' style={{ height: 560 }}>
        {/* Dark gradient background */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950" />

        {/* Subtle grid pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }} />

        {/* Ambient glow */}
        <motion.div
          className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.12) 0%, transparent 70%)' }}
          animate={isGenerating ? { scale: [1, 1.2, 1], opacity: [0.6, 0.3, 0.6] } : { opacity: 0 }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
        />

        <AnimatePresence mode="wait">
          {isGenerating ? (
            <motion.div
              key="gen"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="relative z-10 flex flex-col h-full"
            >
              {/* Top: DNA Helix + waveform */}
              <div className="flex-shrink-0 pt-8 pb-2 flex flex-col items-center">
                <DNAHelix isActive={isGenerating} />
                <div className="mt-4">
                  <Waveform isActive={isGenerating} />
                </div>
              </div>

              {/* Current message area */}
              <div className="flex-shrink-0 px-8 py-4 text-center">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentMsg?.text}
                    initial={{ opacity: 0, y: 10, filter: 'blur(4px)' }}
                    animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                    exit={{ opacity: 0, y: -10, filter: 'blur(4px)' }}
                    transition={{ duration: 0.35 }}
                  >
                    <p className="text-sm font-medium text-white/90">
                      <TypingMessage key={currentMsg?.text} text={currentMsg?.text || ''} />
                    </p>
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Progress bar area */}
              <div className="flex-shrink-0 px-8 pb-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-medium text-white/30 uppercase tracking-widest">Generating</span>
                  <span className="text-[11px] font-mono font-bold text-violet-400 tabular-nums">
                    <SmoothCounter value={progress} />%
                  </span>
                </div>

                {/* Progress bar */}
                <div className="relative h-1 bg-white/5 rounded-full overflow-hidden">
                  {/* Glow under bar */}
                  <motion.div
                    className="absolute -inset-1 rounded-full blur-md"
                    style={{
                      width: `${progress}%`,
                      background: 'linear-gradient(90deg, rgba(139,92,246,0.4), rgba(59,130,246,0.4), rgba(16,185,129,0.3))',
                    }}
                  />
                  <motion.div
                    className="relative h-full rounded-full"
                    style={{
                      width: `${progress}%`,
                      background: 'linear-gradient(90deg, #8b5cf6, #6366f1, #3b82f6, #10b981)',
                      backgroundSize: '200% 100%',
                    }}
                    animate={{ backgroundPosition: ['0% 0%', '100% 0%', '0% 0%'] }}
                    transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                  >
                    {/* Shimmer */}
                    <div className="absolute inset-0 rounded-full overflow-hidden">
                      <motion.div
                        className="absolute inset-0"
                        style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)' }}
                        animate={{ x: ['-100%', '200%'] }}
                        transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                      />
                    </div>
                  </motion.div>
                </div>
              </div>

              {/* Spacer */}
              <div className="flex-1" />

              {/* Bottom pulse line */}
              <div className="flex-shrink-0 px-8 pb-5 pt-2">
                <div className="flex items-center justify-center gap-2">
                  <motion.div
                    className="h-[2px] rounded-full bg-gradient-to-r from-transparent via-violet-500/40 to-transparent"
                    animate={{ width: ['40%', '80%', '40%'], opacity: [0.3, 0.6, 0.3] }}
                    transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                  />
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="idle"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="relative z-10 flex flex-col items-center justify-center h-full gap-4"
            >
              <div className="w-14 h-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center backdrop-blur-sm">
                <Sparkles className="w-6 h-6 text-white/20" />
              </div>
              <p className='text-white/30 text-sm'>Click "Start" to preview</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}


// ─── PAGE ───────────────────────────────────────────────────────────────────

export default function AiGenAnimDebugPage() {
  return (
    <div className='min-h-screen bg-gray-100 py-12 px-8'>
      <div className='max-w-5xl mx-auto'>
        <h1 className='text-2xl font-bold text-gray-900 mb-2 text-center'>AI Generation Animation</h1>
        <p className='text-gray-500 text-sm text-center mb-12'>Compare old and new loading animations</p>

        <div className='grid grid-cols-1 lg:grid-cols-2 gap-12'>
          <div>
            <h2 className='text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4 text-center'>Current</h2>
            <OriginalVersion />
          </div>
          <div>
            <h2 className='text-sm font-semibold text-violet-600 uppercase tracking-wider mb-4 text-center'>New Version</h2>
            <NewVersion />
          </div>
        </div>
      </div>
    </div>
  );
}
